import type { EmailEditorContent, EmailEditorDesign } from '@/types/emailEditorDesign';

const EMAIL_EDITOR_DESIGN_ATTR = 'data-email-editor-design';
const EMBEDDED_DESIGN_PATTERN = /<div[^>]*data-email-editor-design="([^"]*)"[^>]*><\/div>/gi;

const checkDesignContents = (contents: EmailEditorContent[]): boolean => {
  if (!Array.isArray(contents) || contents.length === 0) {
    return false;
  }

  return contents.some((content) => {
    if (!content || typeof content !== 'object') {
      return false;
    }

    if (Array.isArray(content.columns)) {
      return content.columns.some(
        (column) => column && Array.isArray(column.contents) && checkDesignContents(column.contents)
      );
    }

    return true;
  });
};

export const hasEmailEditorDesignContent = ({
  design,
}: {
  design: EmailEditorDesign | null | undefined;
}): boolean => {
  if (!design?.body?.rows) {
    return false;
  }

  return design.body.rows.some(
    (row) =>
      Array.isArray(row.columns) &&
      row.columns.some((column) => column && Array.isArray(column.contents) && checkDesignContents(column.contents))
  );
};

export const stripEmbeddedEmailEditorDesign = ({ html }: { html: string }): string => {
  if (!html) {
    return html;
  }

  return html.replace(EMBEDDED_DESIGN_PATTERN, '');
};

const parseContentDesign = (contentDesign: unknown): EmailEditorDesign | null => {
  if (!contentDesign || typeof contentDesign !== 'object') {
    return null;
  }

  return JSON.parse(JSON.stringify(contentDesign)) as EmailEditorDesign;
};

export const extractEmailEditorDesign = ({
  html,
}: {
  html: string;
}): { html: string; design: EmailEditorDesign | null } => {
  if (!html) {
    return { html: html ?? '', design: null };
  }

  const match = html.match(/<div[^>]*data-email-editor-design="([^"]*)"[^>]*><\/div>/i);
  if (!match?.[1]) {
    return { html, design: null };
  }

  const htmlWithoutDesign = html.replace(match[0], '');

  try {
    return {
      html: htmlWithoutDesign,
      design: parseContentDesign(JSON.parse(decodeURIComponent(match[1]))),
    };
  } catch {
    return {
      html: htmlWithoutDesign,
      design: null,
    };
  }
};

export const embedEmailEditorDesign = ({
  html,
  design,
}: {
  html: string;
  design: EmailEditorDesign | null | undefined;
}): string => {
  const htmlWithoutDesign = stripEmbeddedEmailEditorDesign({ html });
  if (!hasEmailEditorDesignContent({ design })) {
    return htmlWithoutDesign;
  }

  return `${htmlWithoutDesign}<div hidden ${EMAIL_EDITOR_DESIGN_ATTR}="${encodeURIComponent(JSON.stringify(design))}"></div>`;
};

type HtmlContentBlock =
  | { type: 'text'; html: string }
  | { type: 'image'; src: string; alt: string; width?: number; height?: number };

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'META', 'TITLE', 'LINK', 'HEAD', 'NOSCRIPT']);

const isIgnorableImage = ({ element }: { element: HTMLImageElement }): boolean => {
  const src = element.getAttribute('src')?.trim() ?? '';
  if (!src) {
    return true;
  }

  const width = Number(element.getAttribute('width') || element.width || 0);
  const height = Number(element.getAttribute('height') || element.height || 0);
  return width === 1 && height === 1;
};

const appendTextBlock = ({ blocks, html }: { blocks: HtmlContentBlock[]; html: string }): void => {
  if (!html.trim()) {
    return;
  }

  const lastBlock = blocks[blocks.length - 1];
  if (lastBlock?.type === 'text') {
    lastBlock.html += html;
    return;
  }

  blocks.push({ type: 'text', html });
};

const collectBlocksFromNode = ({ node, blocks }: { node: Node; blocks: HtmlContentBlock[] }): void => {
  if (node.nodeType === Node.COMMENT_NODE) {
    return;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    if (!text.trim()) {
      return;
    }
    appendTextBlock({ blocks, html: text });
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = node as HTMLElement;
  if (SKIP_TAGS.has(element.tagName) || element.hasAttribute(EMAIL_EDITOR_DESIGN_ATTR)) {
    return;
  }

  if (element.tagName === 'IMG') {
    const image = element as HTMLImageElement;
    if (isIgnorableImage({ element: image })) {
      return;
    }

    const width = Number(image.getAttribute('width') || image.width || 0);
    const height = Number(image.getAttribute('height') || image.height || 0);
    blocks.push({
      type: 'image',
      src: image.getAttribute('src') ?? '',
      alt: image.getAttribute('alt') ?? '',
      ...(width ? { width } : {}),
      ...(height ? { height } : {}),
    });
    return;
  }

  if (element.querySelector('img')) {
    Array.from(element.childNodes).forEach((child) => collectBlocksFromNode({ node: child, blocks }));
    return;
  }

  if (!element.textContent?.trim()) {
    return;
  }

  const html = element.tagName === 'TD' || element.tagName === 'TH' ? element.innerHTML : element.outerHTML;
  appendTextBlock({ blocks, html });
};

const collectHtmlContentBlocks = ({ html }: { html: string }): HtmlContentBlock[] => {
  if (typeof DOMParser === 'undefined' || !html.trim()) {
    return [];
  }

  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
  const blocks: HtmlContentBlock[] = [];
  Array.from(parsedDocument.body.childNodes).forEach((node) => collectBlocksFromNode({ node, blocks }));
  return blocks;
};

const createTextContent = ({ html }: { html: string }): EmailEditorContent => ({
  type: 'text',
  values: {
    containerPadding: '20px',
    text: html,
    textAlign: 'left',
  },
});

const createImageContent = ({
  src,
  alt,
  width,
  height,
}: {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}): EmailEditorContent => ({
  type: 'image',
  values: {
    containerPadding: '10px',
    textAlign: 'center',
    altText: alt,
    src: {
      url: src,
      autoWidth: true,
      maxWidth: '100%',
      ...(width ? { width } : {}),
      ...(height ? { height } : {}),
    },
  },
});

const contentsFromBlocks = ({ blocks }: { blocks: HtmlContentBlock[] }): EmailEditorContent[] =>
  blocks.map((block) => {
    if (block.type === 'image') {
      return createImageContent(block);
    }
    return createTextContent({ html: block.html });
  });

const createDesignFromContents = ({ contents }: { contents: EmailEditorContent[] }): EmailEditorDesign => ({
  body: {
    rows: [
      {
        cells: [1],
        columns: [{ contents }],
      },
    ],
    values: { backgroundColor: '#ffffff' },
  },
});

export const createDesignFromHtml = ({ html }: { html: string }): EmailEditorDesign => {
  const blocks = collectHtmlContentBlocks({ html });
  const hasImage = blocks.some((block) => block.type === 'image');
  if (!hasImage) {
    return createDesignFromContents({ contents: [createTextContent({ html })] });
  }

  return createDesignFromContents({ contents: contentsFromBlocks({ blocks }) });
};

const splitContentsWithInlineImages = ({ contents }: { contents: EmailEditorContent[] }): EmailEditorContent[] => {
  const nextContents: EmailEditorContent[] = [];

  contents.forEach((content) => {
    if (Array.isArray(content.columns)) {
      content.columns.forEach((column) => {
        if (Array.isArray(column.contents)) {
          column.contents = splitContentsWithInlineImages({ contents: column.contents });
        }
      });
      nextContents.push(content);
      return;
    }

    const textHtml = typeof content.values?.text === 'string' ? content.values.text : '';
    if (content.type !== 'text' || !/<img[\s>]/i.test(textHtml)) {
      nextContents.push(content);
      return;
    }

    const blocks = collectHtmlContentBlocks({ html: textHtml });
    const hasImage = blocks.some((block) => block.type === 'image');
    if (!hasImage) {
      nextContents.push(content);
      return;
    }

    nextContents.push(...contentsFromBlocks({ blocks }));
  });

  return nextContents;
};

/** Old templates stored images inside a text block. Split them so Unlayer can select the image. */
export const splitInlineImagesFromDesign = ({ design }: { design: EmailEditorDesign }): EmailEditorDesign => {
  if (!design.body?.rows) {
    return design;
  }

  let nextDesign: EmailEditorDesign;
  try {
    nextDesign = JSON.parse(JSON.stringify(design)) as EmailEditorDesign;
  } catch {
    return design;
  }

  nextDesign.body?.rows.forEach((row) => {
    row.columns.forEach((column) => {
      if (Array.isArray(column.contents)) {
        column.contents = splitContentsWithInlineImages({ contents: column.contents });
      }
    });
  });

  return nextDesign;
};
